import { create } from 'zustand';
import { getEvents, createEvent, deleteEvent, type Event, type CreateEventRequest } from '@/services/api/events';

interface EventsStore {
  // State
  events: Event[];
  loading: boolean;
  /** The last load failed: `events` is not known to be empty (JUB-157 D2). */
  loadError: boolean;
  creating: boolean;
  deleting: boolean;
  shareEvent: Event | null;
  deleteEventId: string | null;
  showCreateForm: boolean;

  // Actions
  loadEvents: () => Promise<void>;
  createEvent: (eventData: CreateEventRequest) => Promise<Event | null>;
  deleteEvent: (eventUuid: string) => Promise<boolean>;
  setShareEvent: (event: Event | null) => void;
  setDeleteEventId: (eventId: string | null) => void;
  setShowCreateForm: (show: boolean) => void;
  clearShareEvent: () => void;
  clearDeleteEventId: () => void;
  
  // Selectors (computed values)
  getEventById: (uuid: string) => Event | undefined;
  getEventsCount: () => number;
}

export const useEventsStore = create<EventsStore>((set, get) => ({
  // Initial state
  events: [],
  loading: false,
  loadError: false,
  creating: false,
  deleting: false,
  shareEvent: null,
  deleteEventId: null,
  showCreateForm: false,

  // Load events
  loadEvents: async () => {
    try {
      set({ loading: true });
      const eventsData = await getEvents();
      set({ events: eventsData, loading: false, loadError: false });
    } catch (error: unknown) {
      console.error('Failed to load events:', error);
      set({ loading: false, loadError: true });
      throw error;
    }
  },

  // Create event
  createEvent: async (eventData: CreateEventRequest) => {
    try {
      set({ creating: true });
      const newEvent = await createEvent(eventData);
      
      if (!newEvent?.uuid) {
        console.error('Event created but no UUID in response:', newEvent);
        set({ creating: false });
        return null;
      }

      // Add to events list
      set((state) => ({
        events: [...state.events, newEvent],
        creating: false,
        showCreateForm: false,
      }));

      return newEvent;
    } catch (error: unknown) {
      console.error('Failed to create event:', error);
      set({ creating: false });
      throw error;
    }
  },

  // Delete event
  deleteEvent: async (eventUuid: string) => {
    try {
      set({ deleting: true });
      await deleteEvent(eventUuid);
      
      // Remove from events list
      set((state) => ({
        events: state.events.filter((e) => e.uuid !== eventUuid),
        deleting: false,
        deleteEventId: null,
      }));

      return true;
    } catch (error: unknown) {
      console.error('Failed to delete event:', error);
      set({ deleting: false });
      throw error;
    }
  },

  // Set share event
  setShareEvent: (event: Event | null) => {
    set({ shareEvent: event });
  },

  // Clear share event
  clearShareEvent: () => {
    set({ shareEvent: null });
  },

  // Set delete event ID
  setDeleteEventId: (eventId: string | null) => {
    set({ deleteEventId: eventId });
  },

  // Clear delete event ID
  clearDeleteEventId: () => {
    set({ deleteEventId: null });
  },

  // Set show create form
  setShowCreateForm: (show: boolean) => {
    set({ showCreateForm: show });
  },

  // Get event by ID
  getEventById: (uuid: string) => {
    return get().events.find((e) => e.uuid === uuid);
  },

  // Get events count
  getEventsCount: () => {
    return get().events.length;
  },
}));

